import { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import { useTranslate, useNotify, useRedirect, useDataProvider } from 'react-admin';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { ChannelType } from '../../../types/chat.types';
import { QueueSelector } from '../../queue/QueueSelector';
import { SmtpFormFields, defaultSmtpData, encryptionFromPort } from '../email/SmtpFormFields';
import { ImapFormFields, defaultImapData } from '../email/ImapFormFields';
import { channelConnectionApi } from '../../../services/api.service';
import type { SmtpFormData } from '../email/SmtpFormFields';
import type { ImapFormData } from '../email/ImapFormFields';

interface EmailWizardProps {
  onBack: () => void;
}

type EmailProvider = 'gmail' | 'outlook' | 'other';

const EMAIL_PROVIDERS: Record<EmailProvider, { imap: { address: string; port: number; ssl: boolean }; smtp: { address: string; port: number } } | null> = {
  gmail: {
    imap: { address: 'imap.gmail.com', port: 993, ssl: true },
    smtp: { address: 'smtp.gmail.com', port: 587 },
  },
  outlook: {
    imap: { address: 'outlook.office365.com', port: 993, ssl: true },
    smtp: { address: 'smtp.office365.com', port: 587 },
  },
  other: null,
};

const steps = ['channelInfo', 'provider', 'serverConfig', 'finish'];

interface EmailCredentials extends SmtpFormData, ImapFormData {
  email: string;
}

const defaultCredentials: EmailCredentials = {
  email: '',
  ...defaultSmtpData,
  ...defaultImapData,
};

export const EmailWizard = ({ onBack }: EmailWizardProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [defaultQueueId, setDefaultQueueId] = useState<number | null>(null);
  const [credentials, setCredentials] = useState<EmailCredentials>(defaultCredentials);
  const [createdChannelId, setCreatedChannelId] = useState<number | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<EmailProvider | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Test connection state
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'failed'>('idle');
  const [testResult, setTestResult] = useState<{ smtpVerified: boolean; imapVerified: boolean; error?: string } | null>(null);

  const updateCredential = <K extends keyof EmailCredentials>(key: K, value: EmailCredentials[K]) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSelectProvider = (provider: EmailProvider) => {
    setSelectedProvider(provider);
    const config = EMAIL_PROVIDERS[provider];
    if (config) {
      setCredentials((prev) => ({
        ...prev,
        imapEnabled: true,
        imapAddress: config.imap.address,
        imapPort: config.imap.port,
        imapEnableSsl: config.imap.ssl,
        smtpEnabled: true,
        smtpAddress: config.smtp.address,
        smtpPort: config.smtp.port,
      }));
    } else {
      // "other" — reset server fields but keep login/password
      setCredentials((prev) => ({
        ...prev,
        imapEnabled: false,
        imapAddress: '',
        imapPort: 993,
        imapEnableSsl: true,
        smtpEnabled: false,
        smtpAddress: '',
        smtpPort: 587,
      }));
    }
    setActiveStep(2);
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Step 0: Validate channel info
      const newErrors: Record<string, string> = {};
      if (!channelName.trim()) {
        newErrors.channelName = translate('resources.channel.wizard.errors.nameRequired');
      }
      if (!defaultQueueId) {
        newErrors.defaultQueueId = translate('resources.channel.wizard.errors.queueRequired');
      }
      if (!credentials.email.trim()) {
        newErrors.email = translate('resources.channel.wizard.errors.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email.trim())) {
        newErrors.email = translate('resources.channel.wizard.errors.invalidEmail');
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        notify(translate('resources.channel.wizard.errors.fillRequired'), { type: 'error' });
        return;
      }
      setErrors({});
      setActiveStep(1);
    } else if (activeStep === 2) {
      // Step 2: Validate IMAP/SMTP + create channel
      const newErrors: Record<string, string> = {};

      if (credentials.imapEnabled) {
        if (!credentials.imapAddress.trim()) {
          newErrors.imapAddress = translate('resources.channel.wizard.errors.imapAddressRequired');
        }
        if (!credentials.imapLogin.trim()) {
          newErrors.imapLogin = translate('resources.channel.wizard.errors.imapLoginRequired');
        }
        if (!credentials.imapPassword.trim()) {
          newErrors.imapPassword = translate('resources.channel.wizard.errors.imapPasswordRequired');
        }
      }

      if (credentials.smtpEnabled) {
        if (!credentials.smtpAddress.trim()) {
          newErrors.smtpAddress = translate('resources.channel.wizard.errors.smtpAddressRequired');
        }
        if (!credentials.smtpLogin.trim()) {
          newErrors.smtpLogin = translate('resources.channel.wizard.errors.smtpLoginRequired');
        }
        if (!credentials.smtpPassword.trim()) {
          newErrors.smtpPassword = translate('resources.channel.wizard.errors.smtpPasswordRequired');
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        notify(translate('resources.channel.wizard.errors.fillRequired'), { type: 'error' });
        return;
      }

      setErrors({});
      const encryption = encryptionFromPort(credentials.smtpPort);

      setLoading(true);
      try {
        const { data } = await dataProvider.create('channel', {
          data: {
            name: channelName,
            type: ChannelType.EMAIL,
            defaultQueueId,
            credentials: {
              email: credentials.email,
              supportEmail: credentials.email,
              replyToDomain: credentials.email.split('@')[1],
              imapEnabled: credentials.imapEnabled,
              ...(credentials.imapEnabled && {
                imapAddress: credentials.imapAddress,
                imapPort: credentials.imapPort,
                imapLogin: credentials.imapLogin,
                imapPassword: credentials.imapPassword,
                imapEnableSsl: credentials.imapEnableSsl,
              }),
              smtpEnabled: credentials.smtpEnabled,
              ...(credentials.smtpEnabled && {
                smtpAddress: credentials.smtpAddress,
                smtpPort: credentials.smtpPort,
                smtpLogin: credentials.smtpLogin,
                smtpPassword: credentials.smtpPassword,
                smtpEnableSslTls: encryption === 'ssl',
                smtpEnableStarttlsAuto: encryption === 'starttls',
                smtpOpensslVerifyMode: credentials.smtpSelfSignedCert ? 'none' : 'peer',
              }),
            },
          },
        });
        setCreatedChannelId(data.id);
        setActiveStep(3);
        notify('resources.channel.wizard.success.channelCreated', { type: 'success' });
      } catch {
        notify('resources.channel.wizard.errors.createFailed', { type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setActiveStep(1);
    } else if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      onBack();
    }
  };

  const handleFinish = () => {
    if (createdChannelId) {
      redirect('show', 'channel', createdChannelId);
    }
  };

  const handleTestConnection = async () => {
    if (!createdChannelId) return;
    setTestStatus('testing');
    setTestResult(null);
    try {
      const response = await channelConnectionApi.verifyEmailConnection(createdChannelId);
      const result = response.data;
      setTestResult({ smtpVerified: result.smtpVerified, imapVerified: result.imapVerified, error: result.error });
      setTestStatus(result.ok ? 'success' : 'failed');
    } catch {
      setTestStatus('failed');
      setTestResult({ smtpVerified: false, imapVerified: false, error: 'Unexpected error' });
    }
  };

  const noProtocolEnabled = !credentials.imapEnabled && !credentials.smtpEnabled;

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
        {translate('ra.action.back')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(234, 67, 53, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmailIcon sx={{ fontSize: 28, color: '#EA4335' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="medium">
              {translate('resources.channel.wizard.email.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.channel.wizard.email.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step}>
              <StepLabel>{translate(`resources.channel.wizard.steps.${step}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Channel Info */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.email.step1Description')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={translate('resources.channel.fields.name')}
                  value={channelName}
                  onChange={(e) => {
                    setChannelName(e.target.value);
                    if (errors.channelName) clearError('channelName');
                  }}
                  placeholder={translate('resources.channel.wizard.email.namePlaceholder')}
                  helperText={errors.channelName || translate('resources.channel.wizard.email.nameHelper')}
                  error={!!errors.channelName}
                  required
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={translate('resources.channel.wizard.email.emailAddress')}
                  value={credentials.email}
                  onChange={(e) => updateCredential('email', e.target.value)}
                  placeholder="support@yourcompany.com"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>
              <Grid size={12}>
                <QueueSelector
                  value={defaultQueueId}
                  onChange={(queueId) => {
                    setDefaultQueueId(queueId);
                    if (errors.defaultQueueId) clearError('defaultQueueId');
                  }}
                  required
                  allowClear={false}
                  error={!!errors.defaultQueueId}
                  helperText={errors.defaultQueueId || translate('resources.channel.wizard.queueHelper')}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext} disabled={!channelName.trim() || !credentials.email.trim() || !defaultQueueId}>
                {translate('ra.action.next')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 1: Email Provider Selection */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {translate('resources.channel.wizard.email.selectProvider')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.email.selectProviderDescription')}
            </Typography>

            <Grid container spacing={2}>
              {/* Gmail */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    border: selectedProvider === 'gmail' ? 2 : 1,
                    borderColor: selectedProvider === 'gmail' ? 'primary.main' : 'divider',
                  }}
                >
                  <CardActionArea onClick={() => handleSelectProvider('gmail')} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <MailOutlineIcon sx={{ fontSize: 40, color: '#EA4335', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {translate('resources.channel.wizard.email.providerGmail')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.wizard.email.providerGmailDesc')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* Outlook */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    border: selectedProvider === 'outlook' ? 2 : 1,
                    borderColor: selectedProvider === 'outlook' ? 'primary.main' : 'divider',
                  }}
                >
                  <CardActionArea onClick={() => handleSelectProvider('outlook')} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <MailOutlineIcon sx={{ fontSize: 40, color: '#0078D4', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {translate('resources.channel.wizard.email.providerOutlook')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.wizard.email.providerOutlookDesc')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* Other */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    border: selectedProvider === 'other' ? 2 : 1,
                    borderColor: selectedProvider === 'other' ? 'primary.main' : 'divider',
                  }}
                >
                  <CardActionArea onClick={() => handleSelectProvider('other')} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <SettingsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {translate('resources.channel.wizard.email.providerOther')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.channel.wizard.email.providerOtherDesc')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
              <Button onClick={handleBack}>{translate('ra.action.back')}</Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Unified IMAP + SMTP Configuration */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedProvider && selectedProvider !== 'other'
                ? translate('resources.channel.wizard.email.serverConfigDescription')
                : translate('resources.channel.wizard.email.serverConfigDescriptionManual')}
            </Typography>

            {/* IMAP Section */}
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
              {translate('resources.channel.wizard.email.inboundSection')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {translate('resources.channel.wizard.email.imapDescription')}
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Switch checked={credentials.imapEnabled} onChange={(e) => updateCredential('imapEnabled', e.target.checked)} />
              }
              label={translate('resources.channel.wizard.email.enableImap')}
              sx={{ mb: 2 }}
            />

            {credentials.imapEnabled && (
              <ImapFormFields
                data={credentials}
                onChange={updateCredential as <K extends keyof ImapFormData>(key: K, value: ImapFormData[K]) => void}
                errors={errors}
                onClearError={clearError}
              />
            )}

            <Divider sx={{ my: 3 }} />

            {/* SMTP Section */}
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
              {translate('resources.channel.wizard.email.outboundSection')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {translate('resources.channel.wizard.email.smtpDescription')}
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Switch checked={credentials.smtpEnabled} onChange={(e) => updateCredential('smtpEnabled', e.target.checked)} />
              }
              label={translate('resources.channel.wizard.email.enableSmtp')}
              sx={{ mb: 2 }}
            />

            {credentials.smtpEnabled && (
              <SmtpFormFields
                data={credentials}
                onChange={updateCredential as <K extends keyof SmtpFormData>(key: K, value: SmtpFormData[K]) => void}
                errors={errors}
                onClearError={clearError}
              />
            )}

            {/* Warning when neither protocol is enabled */}
            {noProtocolEnabled && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <AlertTitle>
                  {translate('resources.channel.wizard.email.noProtocolWarning')}
                </AlertTitle>
                {translate('resources.channel.wizard.email.noProtocolWarningDetail')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleBack}>{translate('ra.action.back')}</Button>
              <Button variant="contained" onClick={handleNext} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : translate('resources.channel.wizard.createChannel')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Finish */}
        {activeStep === 3 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {translate('resources.channel.wizard.email.successTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {translate('resources.channel.wizard.email.successMessage')}
            </Typography>

            {/* Configuration Summary */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
              <Chip
                label={translate(
                  credentials.imapEnabled ? 'resources.channel.wizard.email.imapConfigured' : 'resources.channel.wizard.email.notConfigured',
                )}
                color={credentials.imapEnabled ? 'success' : 'default'}
                variant={credentials.imapEnabled ? 'filled' : 'outlined'}
                icon={<MailOutlineIcon />}
              />
              <Chip
                label={translate(
                  credentials.smtpEnabled ? 'resources.channel.wizard.email.smtpConfigured' : 'resources.channel.wizard.email.notConfigured',
                )}
                color={credentials.smtpEnabled ? 'success' : 'default'}
                variant={credentials.smtpEnabled ? 'filled' : 'outlined'}
                icon={<MailOutlineIcon />}
              />
            </Box>

            {/* Test Connection */}
            {(credentials.imapEnabled || credentials.smtpEnabled) && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={testStatus === 'testing'}
                  sx={{ mb: 2 }}
                >
                  {testStatus === 'testing' ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {translate('resources.channel.wizard.email.testingConnection')}
                    </>
                  ) : (
                    translate('resources.channel.wizard.email.testConnection')
                  )}
                </Button>

                {testStatus === 'success' && testResult && (
                  <Alert severity="success" sx={{ mx: 'auto', maxWidth: 500, textAlign: 'left' }}>
                    <AlertTitle>
                      {translate('resources.channel.wizard.email.testConnectionSuccess')}
                    </AlertTitle>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {credentials.smtpEnabled && (
                        <Chip
                          size="small"
                          label={translate(
                            testResult.smtpVerified ? 'resources.channel.wizard.email.smtpVerified' : 'resources.channel.wizard.email.smtpNotVerified',
                          )}
                          color={testResult.smtpVerified ? 'success' : 'error'}
                        />
                      )}
                      {credentials.imapEnabled && (
                        <Chip
                          size="small"
                          label={translate(
                            testResult.imapVerified ? 'resources.channel.wizard.email.imapVerified' : 'resources.channel.wizard.email.imapNotVerified',
                          )}
                          color={testResult.imapVerified ? 'success' : 'error'}
                        />
                      )}
                    </Box>
                  </Alert>
                )}

                {testStatus === 'failed' && testResult && (
                  <Alert severity="error" sx={{ mx: 'auto', maxWidth: 500, textAlign: 'left' }}>
                    <AlertTitle>
                      {translate('resources.channel.wizard.email.testConnectionFailed')}
                    </AlertTitle>
                    {testResult.error && (
                      <Typography variant="body2">{testResult.error}</Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {credentials.smtpEnabled && (
                        <Chip
                          size="small"
                          label={translate(
                            testResult.smtpVerified ? 'resources.channel.wizard.email.smtpVerified' : 'resources.channel.wizard.email.smtpNotVerified',
                          )}
                          color={testResult.smtpVerified ? 'success' : 'error'}
                        />
                      )}
                      {credentials.imapEnabled && (
                        <Chip
                          size="small"
                          label={translate(
                            testResult.imapVerified ? 'resources.channel.wizard.email.imapVerified' : 'resources.channel.wizard.email.imapNotVerified',
                          )}
                          color={testResult.imapVerified ? 'success' : 'error'}
                        />
                      )}
                    </Box>
                  </Alert>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {translate('resources.channel.wizard.email.testConnectionNote')}
                </Typography>
              </Box>
            )}

            <Button variant="contained" onClick={handleFinish}>
              {translate('resources.channel.wizard.email.goToChannel')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
